import { FC, ReactNode, useMemo } from "react"
import { Check } from "lucide-react"
import { useIntegrationSettingsForm } from "../../../../hooks"
import { useAppSelector } from "../../../../hooks"
import { selectUserDetails } from "../../../../../redux/slices/auth"
import {
  useFetchLoanOfficerQuery,
  useFetchOrgDetailsQuery
} from "../../../../../redux/queries"
import { getUserBrandId } from "../../../../utils"
import { Integration } from "../../../../../types"
import LeadWorkflowSelector from "../lead-workflow-selector"
import ContactFormSelector from "../contact-form-selector"
import RateCardsSelector from "../rate-cards-selector"
import RateTableSelector from "../rate-table-selector"

interface ModuleTileProps {
  id: string
  label: string
  desc: string
  checked: boolean
  onChange: () => void
  subSelector?: ReactNode
}

const ModuleTile: FC<ModuleTileProps> = ({
  id,
  label,
  desc,
  checked,
  onChange,
  subSelector
}) => {
  const checkboxId = `module-tile-${id}`
  const labelId = `${checkboxId}-label`
  const descriptionId = `${checkboxId}-description`
  const detailsId = `${checkboxId}-details`

  return (
    <div
      className={[
        "rounded-lg border bg-white transition-all",
        checked
          ? "border-primary-500"
          : "border-neutral-200 hover:border-neutral-300",
        "focus-within:ring-2 focus-within:ring-primary-300 focus-within:ring-offset-2"
      ].join(" ")}
    >
      <label
        htmlFor={checkboxId}
        className="flex cursor-pointer select-none items-start gap-5 p-5"
      >
        <input
          id={checkboxId}
          type="checkbox"
          checked={checked}
          onChange={onChange}
          aria-labelledby={labelId}
          aria-describedby={descriptionId}
          aria-controls={subSelector ? detailsId : undefined}
          aria-expanded={subSelector ? checked : undefined}
          className="sr-only"
        />
        <div
          className={[
            "mt-0.5 flex h-[22px] w-[22px] flex-shrink-0 items-center justify-center rounded-[3px] border transition-all",
            checked
              ? "border-primary-500 bg-primary-500"
              : "border-neutral-400 bg-white"
          ].join(" ")}
        >
          {checked && (
            <Check
              size={12}
              aria-hidden="true"
              className="text-white"
              strokeWidth={3}
            />
          )}
        </div>
        <div className="min-w-0 text-sm normal-case">
          <div
            id={labelId}
            className="text-base font-semibold leading-snug text-neutral-900"
          >
            {label}
          </div>
          <div
            id={descriptionId}
            className="mt-1 text-sm leading-snug text-neutral-500"
          >
            {desc}
          </div>
        </div>
      </label>
      {subSelector && checked && (
        <div
          id={detailsId}
          role="region"
          aria-labelledby={labelId}
          className="border-t border-neutral-100 px-5 pb-5 pt-4"
        >
          {subSelector}
        </div>
      )}
    </div>
  )
}

interface Props {
  integration?: Integration
}

const WidgetsCheckboxes = ({ integration }: Props) => {
  const { app_settings, saveAppSetting, saveSetting, values } =
    useIntegrationSettingsForm(integration)

  const { settings } = app_settings ?? {}
  const user = useAppSelector(selectUserDetails)
  const ownerId = values.owner || user?.id || ""
  const isCurrentUser = ownerId === user?.id
  const needsOwnerFetch = !integration?.brand_id && !isCurrentUser && !!ownerId

  const { data: ownerData } = useFetchLoanOfficerQuery(ownerId, {
    skip: !needsOwnerFetch
  })
  const { data: ownerOrg } = useFetchOrgDetailsQuery(
    { id: String(ownerData?.org_id ?? "") },
    { skip: !needsOwnerFetch || !ownerData?.org_id }
  )

  const brandId = useMemo(() => {
    if (integration?.brand_id) return integration.brand_id
    if (values.brand_id) return values.brand_id
    const orgToUse = isCurrentUser ? user?.org : ownerOrg
    if (orgToUse && ownerId) {
      const fallbackBrandId = getUserBrandId(orgToUse, ownerId)
      return fallbackBrandId ?? orgToUse?.brands?.[0]?.id
    }
    return undefined
  }, [
    integration?.brand_id,
    values.brand_id,
    isCurrentUser,
    user?.org,
    ownerOrg,
    ownerId
  ])

  const integrationWithBrandId = useMemo(() => {
    const base = integration ?? values
    if (!base) return undefined
    return { ...base, brand_id: brandId ?? base.brand_id ?? "" }
  }, [integration, values, brandId])

  // ── Derived checked states ──────────────────────────────────────────────
  const mortCalc = app_settings?.mort_calc === 1
  const affordCalc = app_settings?.afford_calc === 1
  const leadWorkflow =
    !!app_settings?.lead_workflow && app_settings.lead_workflow >= 1
  const pricing = app_settings?.pricing === 1
  const contactForm = !!(settings?.contactForm && settings.contactForm > 0)
  const rateTable = settings?.rateTable ?? false
  const loReviews = settings?.loReviews ?? false
  const brandReviews = settings?.brandReviews ?? false
  const aboutBoxes = settings?.aboutBoxes ?? false

  // ── Module definitions ──────────────────────────────────────────────────
  const modules: ModuleTileProps[] = [
    // First rows: All modules with dropdowns
    {
      id: "lead_workflow",
      label: "Lead Workflow",
      desc: "A streamlined way to collect key info and convert traffic into qualified leads.",
      checked: leadWorkflow,
      onChange: () => saveAppSetting({ lead_workflow: leadWorkflow ? 0 : 1 }),
      subSelector:
        leadWorkflow && integrationWithBrandId ? (
          <LeadWorkflowSelector integration={integrationWithBrandId} />
        ) : undefined
    },
    {
      id: "rateTable",
      label: "Rate Table",
      desc: "Shows real-time mortgage rates to boost engagement and conversions.",
      checked: rateTable,
      onChange: () => saveSetting({ rateTable: !rateTable }),
      subSelector:
        rateTable && integration ? (
          <RateTableSelector integration={integration} />
        ) : undefined
    },
    {
      id: "pricing",
      label: "Pricing Cards",
      desc: "Real-time rate display based on fixed assumptions.",
      checked: pricing,
      onChange: () => {
        const next = pricing ? 0 : 1
        saveAppSetting({
          pricing: next,
          settings: {
            ...settings,
            rateCards: !!next,
            ...(pricing && { rateCardsConfigId: null })
          }
        })
      },
      subSelector:
        pricing && integrationWithBrandId ? (
          <RateCardsSelector integration={integrationWithBrandId} />
        ) : undefined
    },
    {
      id: "contactForm",
      label: "Contact Form",
      desc: "Captures inquiries and contact details for prospects to connect.",
      checked: contactForm,
      onChange: () => saveSetting({ contactForm: contactForm ? 0 : 1 }),
      subSelector:
        contactForm && integrationWithBrandId ? (
          <ContactFormSelector integration={integrationWithBrandId} />
        ) : undefined
    },
    // Remaining modules without dropdowns
    {
      id: "mort_calc",
      label: "Mortgage Calculator",
      desc: "Quickly estimate monthly payments, affordability, and loan costs.",
      checked: mortCalc,
      onChange: () => saveAppSetting({ mort_calc: mortCalc ? 0 : 1 })
    },
    {
      id: "afford_calc",
      label: "Affordability Calculator",
      desc: "Estimate your affordable home price based on income and expenses.",
      checked: affordCalc,
      onChange: () => saveAppSetting({ afford_calc: affordCalc ? 0 : 1 })
    },
    {
      id: "loReviews",
      label: "LO Reviews",
      desc: "Pulls & displays loan officer specific reviews from leading review providers.",
      checked: loReviews,
      onChange: () => saveSetting({ loReviews: !loReviews })
    },
    {
      id: "brandReviews",
      label: "Brand Reviews",
      desc: "Pulls and displays brand specific reviews from leading review providers.",
      checked: brandReviews,
      onChange: () => saveSetting({ brandReviews: !brandReviews })
    },
    {
      id: "aboutBoxes",
      label: "About Boxes",
      desc: "Your Mission & team bios showcased on your profile pages.",
      checked: aboutBoxes,
      onChange: () => saveSetting({ aboutBoxes: !aboutBoxes })
    }
  ]

  return (
    <fieldset>
      <legend className="sr-only">Module configuration options</legend>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
        {modules.map(mod => (
          <ModuleTile key={mod.id} {...mod} />
        ))}
      </div>
    </fieldset>
  )
}

export default WidgetsCheckboxes
